import { useMemo } from 'react';

const HEATMAP_MODES = {
  sales: {
    field: 'productAvgSales',
    label: 'Sales',
    positiveColor: [16, 185, 129],   // green
    negativeColor: [239, 68, 68],     // red
    neutralBg: '#f9fafb'
  },
  margin: {
    field: 'productMargin',
    label: 'Margin',
    positiveColor: [16, 185, 129],
    negativeColor: [239, 68, 68],
    neutralBg: '#f9fafb'
  },
  stock: {
    field: null, // computed from current_stock / total_stock_on_fillup
    label: 'Stock',
    positiveColor: [16, 185, 129],
    negativeColor: [239, 68, 68],
    neutralBg: '#f9fafb'
  },
  category: {
    field: 'productCategory',
    label: 'Category',
    neutralBg: '#f9fafb'
  },
  units: {
    field: 'productAvgSales',
    label: 'Units',
    neutralBg: '#f9fafb'
  }
};

// Pastel palette for categories (10 colors)
const CATEGORY_COLORS = [
  'rgba(59, 130, 246, 0.25)',   // blue
  'rgba(16, 185, 129, 0.25)',   // green
  'rgba(245, 158, 11, 0.25)',   // amber
  'rgba(239, 68, 68, 0.25)',    // red
  'rgba(139, 92, 246, 0.25)',   // purple
  'rgba(236, 72, 153, 0.25)',   // pink
  'rgba(6, 182, 212, 0.25)',    // cyan
  'rgba(234, 179, 8, 0.25)',    // yellow
  'rgba(249, 115, 22, 0.25)',   // orange
  'rgba(107, 114, 128, 0.25)'   // gray
];

// Mapa de calor de ventas: una sola tonalidad (crema → naranja tostado), sin azul (selección),
// morado (sugerencias) ni rojo/verde (alertas y tendencia)
const UNITS_RAMP = [
  [0, [254, 243, 226]],
  [0.35, [252, 217, 160]],
  [0.7, [245, 158, 75]],
  [1, [194, 65, 12]]
];
// Una máquina casi parada no se pinta entera de oscuro: la escala nunca termina por debajo de esto
const UNITS_MIN_SCALE_TOP = 0.5;
// Por debajo de esto el orden de los carriles no se repite de un periodo al siguiente
const UNITS_MIN_MACHINE_UNITS = 30;
const UNITS_MIN_OWN_SALES_SHARE = 0.75;

const rampRgb = (t) => {
  const x = Math.min(Math.max(t, 0), 1);
  const i = UNITS_RAMP.findIndex(([stop]) => stop >= x);
  if (i <= 0) return UNITS_RAMP[0][1];
  const [s0, c0] = UNITS_RAMP[i - 1];
  const [s1, c1] = UNITS_RAMP[i];
  const f = (x - s0) / (s1 - s0);
  return c0.map((c, k) => Math.round(c + (c1[k] - c) * f));
};

// Texto claro u oscuro, el que más contraste dé sobre el fondo (WCAG)
const textToneOn = ([r, g, b]) => {
  const lin = (c) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return (1.05 / (L + 0.05)) > ((L + 0.05) / 0.0605) ? 'light' : 'dark';
};

const getSlotValue = (slot, mode) => {
  if (!slot || !slot.name) return null;

  if (mode === 'stock') {
    const total = slot.total_stock_on_fillup || 0;
    if (total <= 0) return null;
    return (slot.current_stock || 0) / total;
  }

  if (mode === 'sales') {
    return slot.salesDeltaPercentage ?? null;
  }

  if (mode === 'margin') {
    return slot.marginDeltaPercentage ?? null;
  }

  return null;
};

// Get absolute display value for a slot in a given mode
const getAbsoluteValue = (slot, mode) => {
  if (!slot || !slot.name) return null;

  if (mode === 'sales') {
    return slot.productAvgSales > 0 ? slot.productAvgSales : null;
  }

  if (mode === 'margin') {
    return slot.productMargin > 0 ? slot.productMargin : null;
  }

  if (mode === 'stock') {
    const total = slot.total_stock_on_fillup || 0;
    if (total <= 0) return null;
    return (slot.current_stock || 0) / total;
  }

  return null;
};

const useHeatmapColors = (slots, mode, period = 14) => {
  return useMemo(() => {
    if (!slots || !mode || mode === 'none' || mode === 'products') {
      return { colorMap: {}, valueMap: {}, absoluteValueMap: {}, categoryMap: {}, minVal: 0, maxVal: 0 };
    }

    const config = HEATMAP_MODES[mode];
    if (!config) return { colorMap: {}, valueMap: {}, absoluteValueMap: {}, categoryMap: {}, minVal: 0, maxVal: 0 };

    const colorMap = {};
    const valueMap = {};
    const absoluteValueMap = {};
    const categoryMap = {};
    let minVal = Infinity;
    let maxVal = -Infinity;

    // Category mode: assign colors by productCategory
    if (mode === 'category') {
      const categories = new Set();
      slots.forEach(slot => {
        if (slot?.productCategory) categories.add(slot.productCategory);
      });

      const categoryColorIndex = {};
      let idx = 0;
      categories.forEach(cat => {
        categoryColorIndex[cat] = CATEGORY_COLORS[idx % CATEGORY_COLORS.length];
        idx++;
      });

      slots.forEach(slot => {
        const key = `${slot.row}-${slot.column}`;
        const cat = slot?.productCategory;
        if (cat) {
          colorMap[key] = categoryColorIndex[cat];
          categoryMap[key] = cat;
        } else {
          colorMap[key] = config.neutralBg;
          categoryMap[key] = null;
        }
        valueMap[key] = null;
        absoluteValueMap[key] = null;
      });

      // Build a legend-ready structure
      categoryMap._legend = Object.entries(categoryColorIndex).map(([name, color]) => ({ name, color }));

      return { colorMap, valueMap, absoluteValueMap, categoryMap, minVal: 0, maxVal: 0 };
    }

    // Units mode: u/d de cada carril sobre la escala de ESTA máquina, comprimida con raíz cuadrada
    // para que unos pocos carriles muy vendedores no dejen el resto en el mismo tono pálido
    if (mode === 'units') {
      const textMap = {};
      const filled = slots.filter(slot => slot?.name);

      // Unidades por carril: las vendidas desde el propio carril; los carriles sin ventas propias se
      // reparten lo que el producto vendió en la máquina y no se pudo atribuir a ningún carril
      const byProduct = new Map();
      filled.forEach(slot => byProduct.set(slot.d_product_id, [...(byProduct.get(slot.d_product_id) || []), slot]));
      const laneUnits = new Map();
      let machineUnits = 0;
      let sellingLanes = 0;
      let ownSalesLanes = 0;
      byProduct.forEach(lanes => {
        const matched = lanes.reduce((sum, slot) => sum + (slot.laneUnits || 0), 0);
        const unmatched = lanes.filter(slot => !(slot.laneUnits > 0));
        const residual = unmatched.length ? Math.max(0, (lanes[0].productUnits || 0) - matched) / unmatched.length : 0;
        lanes.forEach(slot => {
          const units = slot.laneUnits > 0 ? slot.laneUnits : residual;
          laneUnits.set(slot, units);
          machineUnits += units;
          if (units > 0) sellingLanes++;
          // A product in a single lane is attributable even when its sales carry no lane
          if (units > 0 && (slot.laneUnits > 0 || lanes.length === 1)) ownSalesLanes++;
        });
      });

      // Sin ventas suficientes o sin poder atribuirlas a carriles el color sería ruido: tarjetas sin
      // pintar (el tono base de la rampa diría "0 ventas") y una nota
      const heatNote = machineUnits < UNITS_MIN_MACHINE_UNITS
        ? 'lowVolume'
        : (ownSalesLanes / Math.max(sellingLanes, 1) < UNITS_MIN_OWN_SALES_SHARE ? 'lanesUnknown' : null);

      const perDay = filled.map(slot => laneUnits.get(slot) / period).sort((a, b) => a - b);
      const p95 = perDay[Math.min(perDay.length - 1, Math.floor(perDay.length * 0.95))] || 0;
      const top = Math.max(UNITS_MIN_SCALE_TOP, p95);

      filled.forEach(slot => {
        const key = `${slot.row}-${slot.column}`;
        const unitsPerDay = laneUnits.get(slot) / period;
        if (!heatNote) {
          const rgb = rampRgb(Math.sqrt(unitsPerDay / top));
          colorMap[key] = `rgb(${rgb.join(',')})`;
          textMap[key] = textToneOn(rgb);
        }
        valueMap[key] = unitsPerDay;
        absoluteValueMap[key] = unitsPerDay;
      });
      return { colorMap, valueMap, absoluteValueMap, categoryMap, textMap, heatNote, minVal: 0, maxVal: top };
    }

    if (mode === 'stock') {
      // Stock mode: 0-1 ratio, green=high, red=low
      slots.forEach(slot => {
        const key = `${slot.row}-${slot.column}`;
        const val = getSlotValue(slot, mode);
        const absVal = getAbsoluteValue(slot, mode);
        valueMap[key] = val;
        absoluteValueMap[key] = absVal;
        if (val === null) {
          colorMap[key] = config.neutralBg;
          return;
        }
        if (val < minVal) minVal = val;
        if (val > maxVal) maxVal = val;
        // val is 0..1, higher is better
        const intensity = Math.min(Math.max(val, 0), 1);
        if (intensity >= 0.5) {
          const alpha = 0.15 + ((intensity - 0.5) * 2) * 0.5;
          colorMap[key] = `rgba(${config.positiveColor.join(',')}, ${alpha.toFixed(2)})`;
        } else {
          const alpha = 0.15 + ((0.5 - intensity) * 2) * 0.5;
          colorMap[key] = `rgba(${config.negativeColor.join(',')}, ${alpha.toFixed(2)})`;
        }
      });
    } else {
      // Sales/Margin mode: delta percentage, positive=green, negative=red
      const values = slots
        .map(s => getSlotValue(s, mode))
        .filter(v => v !== null);

      if (values.length === 0) return { colorMap: {}, valueMap: {}, absoluteValueMap: {}, categoryMap: {}, minVal: 0, maxVal: 0 };

      const maxAbs = Math.max(...values.map(Math.abs), 1);

      slots.forEach(slot => {
        const key = `${slot.row}-${slot.column}`;
        const val = getSlotValue(slot, mode);
        const absVal = getAbsoluteValue(slot, mode);
        valueMap[key] = val;
        absoluteValueMap[key] = absVal;
        if (val === null) {
          colorMap[key] = config.neutralBg;
          return;
        }
        if (val < minVal) minVal = val;
        if (val > maxVal) maxVal = val;
        const normalized = Math.min(Math.abs(val) / maxAbs, 1);
        const alpha = 0.15 + normalized * 0.55;
        const rgb = val >= 0 ? config.positiveColor : config.negativeColor;
        colorMap[key] = `rgba(${rgb.join(',')}, ${alpha.toFixed(2)})`;
      });
    }

    // Fix infinity values when no valid data
    if (minVal === Infinity) minVal = 0;
    if (maxVal === -Infinity) maxVal = 0;

    return { colorMap, valueMap, absoluteValueMap, categoryMap, minVal, maxVal };
  }, [slots, mode, period]);
};

export { HEATMAP_MODES, UNITS_RAMP };
export default useHeatmapColors;
